import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import type { User } from './lib/supabase';
import { Login } from './components/Login';
import { Lobby } from './components/Lobby';
import { SetUsername } from './components/SetUsername';
import { PokerTableView } from './components/PokerTableView';
import { TableDock } from './components/TableDock';
import { getSession, onAuthStateChange, signOut } from './lib/supabase';
import { getGameClient, fetchTables, fetchMe, createTable } from './lib/gameClient';
import type { TableConfig } from './lib/gameClient';
import { saveTableSession, getTableSessions, removeTableSession, clearTableSession } from './lib/tableSession';
import { MultiTableProvider, useMultiTable } from './contexts/MultiTableContext';
import type { Room } from 'colyseus.js';
import type { PokerState } from './lib/pokerSchema';
import './index.css';

/* ─────────────────────────────────────────────────────────────────
   AppContent — auth, profile, join/reconnect orchestration
   ───────────────────────────────────────────────────────────────── */

function AppContent() {
  const { tables, activeTableId, view, registerTable, unregisterTable, switchToTable, goToLobby } =
    useMultiTable();

  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [profile, setProfile] = useState<{ balance: number; username: string | null } | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState(false);
  const [joining, setJoining] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  // Prevent double-reconnect across renders
  const reconnectAttempted = useRef(false);

  /* ── Auth ── */
  useEffect(() => {
    getSession().then((session) => {
      setUser(session?.user ?? null);
      setAuthChecked(true);
    });
    return onAuthStateChange((u) => setUser(u));
  }, []);

  /* ── Profile ── */
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    setProfile(null);
    setProfileLoadError(false);
    setProfileLoading(true);

    const load = async () => {
      let token: string | undefined;
      for (let i = 0; i < 3; i++) {
        const s = await getSession();
        token = s?.session?.access_token;
        if (token) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!token) {
        setProfile({ balance: 0, username: null });
        return;
      }
      const me = await fetchMe(token);
      setProfile(me ? { balance: me.balance, username: me.username } : { balance: 0, username: null });
    };

    load()
      .catch(async (err: Error & { status?: number }) => {
        if (err?.status === 401) {
          await signOut();
          setUser(null);
        }
        setProfile((prev) => (prev?.username ? prev : { balance: 0, username: null }));
        setProfileLoadError(true);
      })
      .finally(() => setProfileLoading(false));
  // Intentionally keyed on user.id only — Supabase TOKEN_REFRESHED events
  // fire frequently and should not reset profile.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const reloadProfile = () => {
    if (!user) return;
    getSession()
      .then((s) => s?.session?.access_token)
      .then((token) => (token ? fetchMe(token) : null))
      .then((me) => {
        if (me) setProfile({ balance: me.balance, username: me.username });
      })
      .catch(() => {});
  };

  /* ── Reconnect to all saved sessions on startup ── */
  useEffect(() => {
    if (!authChecked || !user || reconnectAttempted.current) return;
    reconnectAttempted.current = true;

    const sessions = getTableSessions().filter((s) => s.userId === user.id);
    if (sessions.length === 0) {
      // No saved sessions — determine initial view from URL
      const path = window.location.pathname;
      const m = path.match(/^\/table\/.+$/);
      if (m) {
        // URL points to a table but no session exists — go to lobby
        history.replaceState(null, '', '/');
      }
      return;
    }

    setReconnecting(true);

    // Determine which table the URL is pointing at (if any)
    const urlPath = window.location.pathname;
    const urlTableMatch = urlPath.match(/^\/table\/(.+)$/);
    const urlTableId = urlTableMatch?.[1] ?? null;

    const client = getGameClient();
    const currentUserId = user.id;

    Promise.allSettled(
      sessions.map(async (saved) => {
        const shouldActivate = saved.tableId === urlTableId;

        const tryReconnect = async (): Promise<Room<PokerState>> => {
          try {
            return await client.reconnect(saved.reconnectionToken) as Room<PokerState>;
          } catch {
            // Reconnect token expired — try a fresh join
            const session = await getSession();
            const token = session?.session?.access_token ?? 'dev-token';
            return client.joinOrCreate('poker', {
              tableId: saved.tableId,
              token,
              accessToken: token,
            }) as Promise<Room<PokerState>>;
          }
        };

        const room = await tryReconnect();

        // Persist new token
        const newToken =
          (room as unknown as { reconnectionToken?: string }).reconnectionToken ??
          saved.reconnectionToken;
        if (typeof newToken === 'string' && newToken.includes(':')) {
          saveTableSession({
            reconnectionToken: newToken,
            tableId: saved.tableId,
            config: saved.config,
            userId: currentUserId,
          });
        }

        registerTable(saved.tableId, saved.config, room, currentUserId, {
          activate: shouldActivate,
        });

        reloadProfile();
      }),
    ).finally(() => {
      setReconnecting(false);
      // If the URL pointed at a table but reconnect didn't activate it
      // (e.g. it failed), fall back to lobby
      if (urlTableId && !tables.has(urlTableId)) {
        history.replaceState(null, '', '/');
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, user?.id]);

  /* ── Join ── */
  const handleJoinTableWithConfig = async (tableId: string, config: TableConfig) => {
    // Already connected — just switch
    if (tables.has(tableId)) {
      switchToTable(tableId);
      return;
    }

    setJoinError(null);
    setJoining(true);
    try {
      const session = await getSession();
      const token = session?.session?.access_token ?? 'dev-token';
      const userId = session?.user?.id;
      if (!userId) throw new Error('Not signed in');

      const client = getGameClient();
      const room = (await client.joinOrCreate('poker', {
        tableId,
        token,
        accessToken: token,
      })) as Room<PokerState>;

      // Guard: if the server routed us to a room we're already connected to
      // (e.g. server filterBy doesn't isolate by tableId), switch to the existing
      // entry instead of double-registering the same Colyseus room.
      const newRoomServerId = (room as unknown as { id?: string }).id;
      if (newRoomServerId) {
        const duplicate = Array.from(tables.values()).find(
          (e) => (e.room as unknown as { id?: string }).id === newRoomServerId,
        );
        if (duplicate) {
          try { (room as unknown as { leave: (c: boolean) => void }).leave(false); } catch { /* ignore */ }
          switchToTable(duplicate.id);
          return;
        }
      }

      // Persist session — retry briefly while token propagates
      const trySave = () => {
        const rcToken = (room as unknown as { reconnectionToken?: string }).reconnectionToken;
        if (typeof rcToken === 'string' && rcToken.includes(':')) {
          saveTableSession({ reconnectionToken: rcToken, tableId, config, userId });
          return true;
        }
        return false;
      };
      if (!trySave()) {
        const interval = setInterval(() => {
          if (trySave()) clearInterval(interval);
        }, 100);
        setTimeout(() => clearInterval(interval), 2000);
      }

      registerTable(tableId, config, room, userId, { activate: true });
      reloadProfile();
    } catch (e) {
      setJoinError((e as Error).message ?? 'Failed to join table');
    } finally {
      setJoining(false);
    }
  };

  const handleJoinTable = async (tableId: string) => {
    const tableList = await fetchTables();
    const config = tableList.find((t) => t.id === tableId);
    if (!config) {
      setJoinError('Table not found');
      return;
    }
    await handleJoinTableWithConfig(tableId, config);
  };

  const handleCreateAndJoin = async (config: {
    maxSeats: 2 | 6;
    smallBlind: number;
    bigBlind: number;
    minBuyIn: number;
    maxBuyIn: number;
  }) => {
    setJoinError(null);
    setCreating(true);
    try {
      const session = await getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Not signed in');

      const result = await createTable(token, config);
      const tableConfig: TableConfig = {
        id: result.tableId,
        smallBlind: result.smallBlind,
        bigBlind: result.bigBlind,
        minBuyIn: result.minBuyIn,
        maxBuyIn: result.maxBuyIn,
        maxSeats: result.maxSeats,
        stakes: result.stakes,
      };
      await handleJoinTableWithConfig(result.tableId, tableConfig);
    } catch (e) {
      setJoinError((e as Error).message ?? 'Failed to create table');
    } finally {
      setCreating(false);
    }
  };

  /* ── Logout ── */
  const handleLogout = async () => {
    // Leave all rooms
    for (const entry of tables.values()) {
      try {
        (entry.room as unknown as { leave: (c: boolean) => void }).leave(true);
      } catch {
        // ignore
      }
      removeTableSession(entry.id);
    }
    clearTableSession();
    reconnectAttempted.current = false;
    await signOut();
    setUser(null);
    setProfile(null);
    history.replaceState(null, '', '/');
    window.location.reload(); // Cleanest reset for multi-table state
  };

  /* ─── Early returns: loading / auth / username setup ─── */

  if (!authChecked || reconnecting) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <p className="text-text-muted">{reconnecting ? 'Reconnecting to tables…' : 'Loading…'}</p>
      </div>
    );
  }

  if (!user) return <Login />;

  if (profileLoading || (profile === null && user)) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <p className="text-text-muted">Loading…</p>
      </div>
    );
  }

  const needsUsername = !profile?.username || String(profile.username).trim() === '';
  if (user && profile && needsUsername) {
    if (profileLoadError) {
      return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background gap-4 p-4">
          <p className="text-text-muted text-center">Could not load your profile.</p>
          <button
            type="button"
            onClick={() => {
              setProfileLoadError(false);
              setProfileLoading(true);
              getSession()
                .then((s) => s?.session?.access_token)
                .then((token) => (token ? fetchMe(token) : null))
                .then((me) => {
                  setProfileLoadError(false);
                  if (me) setProfile({ balance: me.balance, username: me.username });
                })
                .catch(() => setProfileLoadError(true))
                .finally(() => setProfileLoading(false));
            }}
            className="text-primary hover:underline font-medium"
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      <SetUsername
        onComplete={(username) => {
          setProfile((prev) => (prev ? { ...prev, username } : { balance: 0, username }));
          getSession()
            .then((s) => s?.session?.access_token)
            .then((token) => (token ? fetchMe(token) : null))
            .then((me) => {
              if (me) setProfile({ balance: me.balance, username: me.username });
            })
            .catch(() => {});
        }}
        onSignOut={handleLogout}
      />
    );
  }

  /* ─── Main layout ─── */
  const balanceCents = profile?.balance != null ? Math.round(profile.balance * 100) : 0;
  // Only tables where the user is seated (myStack >= 0); same logic as TableDock "My Tables"
  const activeTables = new Set(
    [...tables.entries()]
      .filter(([, e]) => e.snapshot.myStack >= 0)
      .map(([id]) => id),
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      {/* ── Join error toast ── */}
      {joinError && (
        <div
          className="shrink-0 px-6 py-2.5 bg-error/20 text-error text-sm text-center flex items-center justify-between gap-4"
          role="alert"
        >
          <span>{joinError}</span>
          <button
            type="button"
            onClick={() => setJoinError(null)}
            className="text-error/70 hover:text-error text-lg leading-none"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* ── Content area: lobby + all table views stacked, one visible at a time ── */}
      <div className="flex-1 relative min-h-0">
        {/* Lobby */}
        <div
          className="absolute inset-0 overflow-auto"
          style={{ display: view === 'lobby' ? 'block' : 'none' }}
        >
          <Lobby
            balance={profile?.balance ?? 0}
            userDisplayName={profile?.username ?? user.email ?? ''}
            onSignOut={handleLogout}
            onJoinTable={handleJoinTable}
            onCreateAndJoin={handleCreateAndJoin}
            joining={joining}
            creating={creating}
            activeTables={activeTables}
            onGoToTable={switchToTable}
          />
        </div>

        {/* All table views — mounted always once joined, toggled by display */}
        {Array.from(tables.values()).map((entry) => {
          const isVisible = view === 'table' && activeTableId === entry.id;
          return (
            <div
              key={entry.id}
              className="absolute inset-0 flex flex-col"
              style={{ display: isVisible ? 'flex' : 'none' }}
            >
              <PokerTableView
                room={entry.room}
                userId={user.id}
                balance={balanceCents}
                tableConfig={{
                  minBuyIn: entry.config.minBuyIn,
                  maxBuyIn: entry.config.maxBuyIn,
                  smallBlind: entry.config.smallBlind,
                  bigBlind: entry.config.bigBlind,
                  maxSeats: entry.config.maxSeats,
                  stakes: entry.config.stakes,
                }}
                onGoToLobby={goToLobby}
                onLeaveTable={() => unregisterTable(entry.id)}
                onBalanceChange={(dollars) =>
                  setProfile((prev) => (prev ? { ...prev, balance: dollars } : null))
                }
                onTableClosing={(reason) => {
                  if (reason) setJoinError(reason);
                  unregisterTable(entry.id);
                }}
              />
            </div>
          );
        })}
      </div>

      {/* ── Persistent table dock ── */}
      {tables.size > 0 && <TableDock />}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   App root
   ───────────────────────────────────────────────────────────────── */

export function App() {
  return (
    <BrowserRouter>
      <MultiTableProvider>
        <AppContent />
      </MultiTableProvider>
    </BrowserRouter>
  );
}

let rootInstance: ReturnType<typeof ReactDOM.createRoot> | null = null;

/** Mount the shared UI into a DOM node. Used by apps/desktop and apps/web. */
export function mountApp(rootElement: HTMLElement) {
  if (rootInstance) {
    rootInstance.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
    );
    return;
  }
  rootInstance = ReactDOM.createRoot(rootElement);
  rootInstance.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}

// Auto-mount when run standalone (e.g. pnpm dev in packages/ui)
const root = document.getElementById('root');
if (root) {
  mountApp(root);
}

export { PokerTable, Card, Button, Login, Lobby, PokerTableView } from './components';
export type { PokerTableProps, CardProps, ButtonProps, LoginProps, LobbyProps, CashGameTable, Suit, Rank } from './components';
